<template>
    <div class="ys-log-management">
        <Card :bordered="false" dis-hover>
            <!-- 高级搜索 -->
            <div class="search-wrapper">
                <search-card
                    :form="mainTable.searchForm"
                    :table-load-event="getYSLogs"
                    :table-search="mainTable.tableSearch"
                    @on-reset="handleSearchReset" />
            </div>

            <Divider />

            <!-- 统计信息卡片 -->
            <Row :gutter="20" class="stat-row">
                <Col :xs="24" :sm="12" :md="6" v-for="stat in statistics" :key="stat.label">
                    <Card class="stat-card" :class="stat.class" shadow>
                        <div class="stat-content">
                            <div class="stat-icon-box">
                                <Icon :type="stat.icon" size="24" />
                            </div>
                            <div class="stat-info">
                                <div class="stat-value">{{ stat.value }}</div>
                                <div class="stat-label">{{ stat.label }}</div>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            <!-- 工具栏 -->
            <div class="global-tool-bar">
                <div class="left-bar">
                    <span class="title">用友同步日志列表</span>
                    <Tag v-if="filterCount > 0" color="primary" class="filter-tag" checkable @on-change="clearAllFilters">
                        已筛选 {{ filterCount }} 条 <Icon type="md-close" />
                    </Tag>
                </div>
                <div class="right-bar">
                    <Tooltip content="刷新数据" placement="top">
                        <Button
                            type="primary"
                            ghost
                            shape="circle"
                            icon="md-refresh"
                            :loading="mainTable.loading"
                            @click="refreshData" />
                    </Tooltip>
                </div>
            </div>

            <!-- 数据表格 -->
            <div class="table-container">
                <stand-pagination-table
                    :columns="mainTable.columns"
                    :data="mainTable.data"
                    :loading="mainTable.loading"
                    :table-search="mainTable.tableSearch"
                    :table-load-event="getYSLogs"
                    :autoHeight="true"
                    :showPage="true"
                    @on-row-click="handleRowClick"
                    ref="paginationTable" />
            </div>
        </Card>

        <!-- 详情模态框 -->
        <YSLogDetailModal ref="ysLogDetailModal" />
    </div>
</template>

<script>
import search from "@/libs/search";
import StandPaginationTable from '@/components/pagination-table/pagination-table.vue';
import { getYSLogList } from '@/api/modules/system-management/ys-log-management';
import SearchCard from '@/components/searchCard/searchCard.vue';
import YSLogDetailModal from './ys-log-detail-modal.vue';

export default {
    name: "YSLogManagement",
    components: {
        StandPaginationTable,
        SearchCard,
        YSLogDetailModal
    },

    data() {
        return {
            mainTable: {
                columns: [
                    {
                        title: "时间",
                        key: "timestamp",
                        width: 170,
                        fixed: 'left',
                        align: 'center',
                        render: (h, params) => {
                            const timestamp = params.row.timestamp;
                            if (!timestamp) return h('span', '-');

                            const date = new Date(timestamp);
                            const dateStr = date.toLocaleDateString('zh-CN').replace(/\//g, '-');
                            const timeStr = date.toLocaleTimeString('zh-CN', { hour12: false });

                            return h('div', { class: 'time-column' }, [
                                h('div', { class: 'date-text' }, dateStr),
                                h('div', { class: 'time-text' }, timeStr)
                            ]);
                        }
                    },
                    {
                        title: "业务类型",
                        key: "businessType",
                        width: 120,
                        align: 'center',
                        filters: [
                            { label: '销售订单', value: 'VoucherOrder' },
                            { label: '调拨订单', value: 'TransferApply' },
                            { label: '其他', value: 'Other' }
                        ],
                        filterMultiple: true,
                        filterMethod: (value, row) => row.businessType === value,
                        render: (h, params) => {
                            const businessType = params.row.businessType;
                            const typeMap = {
                                'VoucherOrder': { text: '销售订单', color: 'blue' },
                                'TransferApply': { text: '调拨订单', color: 'green' },
                                'Other': { text: '其他', color: 'default' }
                            };
                            const config = typeMap[businessType] || { text: businessType || '-', color: 'default' };

                            return h('Tag', { props: { color: config.color } }, config.text);
                        }
                    },
                    {
                        title: "业务编号",
                        key: "businessCode",
                        minWidth: 160,
                        render: (h, params) => {
                            const businessCode = params.row.businessCode;
                            if (!businessCode) return h('span', '-');

                            return h('div', { class: 'copyable-cell' }, [
                                h('span', { class: 'code-text' }, businessCode),
                                h('Tooltip', { props: { content: '复制编号', placement: 'top' } }, [
                                    h('Icon', {
                                        props: { type: 'md-copy', size: 14 },
                                        class: 'copy-icon',
                                        on: {
                                            click: (e) => {
                                                e.stopPropagation();
                                                this.copyToClipboard(businessCode);
                                            }
                                        }
                                    })
                                ])
                            ]);
                        }
                    },
                    {
                        title: "客户信息",
                        key: "customerName",
                        width: 180,
                        render: (h, params) => {
                            const { customerName, customerPhone } = params.row;
                            if (!customerName && !customerPhone) return h('span', '-');

                            return h('div', { class: 'customer-info' }, [
                                customerName ? h('div', { class: 'name' }, customerName) : null,
                                customerPhone ? h('div', { class: 'phone' }, [
                                    h('Icon', { props: { type: 'md-phone-portrait', size: 12 } }),
                                    h('span', customerPhone)
                                ]) : null
                            ]);
                        }
                    },
                    {
                        title: "用户",
                        key: "userName",
                        width: 120,
                        align: 'center'
                    },
                    {
                        title: "涉及金额",
                        key: "totalMoneyActual",
                        width: 130,
                        align: 'right',
                        sortable: true,
                        render: (h, params) => {
                            const amount = params.row.totalMoneyActual;
                            if (amount === undefined || amount === null) return h('span', '-');

                            return h('span', {
                                class: ['amount-text', amount > 0 ? 'positive' : '']
                            }, `¥ ${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`);
                        }
                    },
                    {
                        title: "用友状态",
                        key: "ysOrderCode",
                        width: 160,
                        align: 'center',
                        render: (h, params) => {
                            const { ysOrderCode, ysOrderId } = params.row;

                            if (!ysOrderCode && !ysOrderId) {
                                return h('Tag', { props: { color: 'orange' } }, '未同步');
                            }

                            return h('div', { class: 'ys-info-cell' }, [
                                h('div', { class: 'order-code' }, ysOrderCode || '-'),
                                ysOrderId ? h('div', { class: 'order-id' }, `ID: ${ysOrderId}`) : null
                            ]);
                        }
                    },
                    {
                        title: "日志摘要",
                        key: "errorMessage",
                        minWidth: 200,
                        ellipsis: true,
                        tooltip: true,
                        render: (h, params) => {
                            const msg = params.row.errorMessage;
                            if (!msg) return h('span', { style: { color: '#19be6b' } }, '同步成功');
                            return h('span', { style: { color: '#ed4014' } }, msg);
                        }
                    },
                    {
                        title: "操作",
                        key: "action",
                        width: 80,
                        align: 'center',
                        fixed: 'right',
                        render: (h, params) => {
                            return h('Button', {
                                props: {
                                    type: 'text',
                                    size: 'small',
                                    icon: 'md-eye'
                                },
                                class: 'view-btn',
                                on: {
                                    click: (e) => {
                                        e.stopPropagation();
                                        this.showDetail(params.row);
                                    }
                                }
                            }, '详情');
                        }
                    }
                ],
                data: [],
                searchForm: [
                    { label: '业务类型', type: 'Select', value: '', name: 'businessType', sql: 'eq', placeholder: '业务类型', options: [{ label: '销售订单', value: 'VoucherOrder' }, { label: '其他', value: 'Other' }] },
                    { label: '业务编号', type: 'Input', value: '', name: 'businessCode', sql: 'like', placeholder: '编号模糊搜索' },
                    { label: '客户姓名', type: 'Input', value: '', name: 'customerName', sql: 'like', placeholder: '姓名模糊搜索' },
                    { label: '客户电话', type: 'Input', value: '', name: 'customerPhone', sql: 'eq', placeholder: '精确匹配' },
                    { label: '用户名', type: 'Input', value: '', name: 'userName', sql: 'like', placeholder: '用户名模糊搜索' },
                    { label: '开始日期', type: 'Date', value: '', name: 'startDate', sql: 'eq', placeholder: '开始日期', format: 'yyyy-MM-dd' },
                    { label: '结束日期', type: 'Date', value: '', name: 'endDate', sql: 'eq', placeholder: '结束日期', format: 'yyyy-MM-dd' }
                ],
                loading: false,
                tableSearch: search.getSearchParams()
            }
        };
    },

    computed: {
        totalLogs() {
            return this.mainTable.tableSearch.limit.conditions.total || 0;
        },
        errorCount() {
            return this.mainTable.data.filter(item => item.errorMessage || item.errorType).length;
        },
        voucherOrderCount() {
            return this.mainTable.data.filter(item => item.businessType === 'VoucherOrder').length;
        },
        totalAmount() {
            const data = this.mainTable.data;
            if (data.length === 0) return '0.00';
            const total = data.reduce((sum, item) => sum + (item.totalMoneyActual || 0), 0);
            return total.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        filterCount() {
            return this.mainTable.tableSearch.limit.conditions.total || 0;
        },
        statistics() {
            return [
                { label: '总日志数', value: this.totalLogs, icon: 'md-list-box', class: 'stat-primary' },
                { label: '同步失败', value: this.errorCount, icon: 'md-alert', class: 'stat-error' },
                { label: '销售订单', value: this.voucherOrderCount, icon: 'md-cart', class: 'stat-warning' },
                { label: '当页涉及金额', value: `¥ ${this.totalAmount}`, icon: 'md-cash', class: 'stat-success' }
            ];
        }
    },

    methods: {
        getYSLogs() {
            this.mainTable.loading = true;
            this.syncSearchFormToFilters();

            getYSLogList(this.mainTable.tableSearch)
                .then((res) => {
                    this.mainTable.loading = false;
                    if (res.code === 0) {
                        const { records, totalCount } = res.data || {};
                        this.mainTable.data = Array.isArray(records) ? records : (Array.isArray(res.data) ? res.data : []);
                        search.globalSetFilterRecordCount(this.mainTable.tableSearch, totalCount || this.mainTable.data.length);
                    } else {
                        this.mainTable.data = [];
                        this.$Message.error(res.message || '获取用友日志失败');
                    }
                })
                .catch((error) => {
                    this.mainTable.loading = false;
                    console.error('获取用友日志失败:', error);
                    this.mainTable.data = [];
                    this.$Message.error('获取用友日志失败，请检查网络连接');
                });
        },

        handleSearchReset() {
            this.clearAllFilters();
        },

        clearAllFilters() {
            if (this.mainTable.tableSearch.filters) {
                this.mainTable.tableSearch.filters.conditions = [];
            }
            this.mainTable.searchForm.forEach(item => { item.value = ''; });
            this.refreshData();
        },

        syncSearchFormToFilters() {
            if (!this.mainTable.tableSearch.filters) {
                this.mainTable.tableSearch.filters = { enable: true, conditions: [] };
            }
            const conditions = [];
            this.mainTable.searchForm.forEach(item => {
                if (item.value !== '' && item.value !== null && item.value !== undefined) {
                    conditions.push({
                        Name: item.name,
                        Value: item.value,
                        Type: item.sql || 'eq'
                    });
                }
            });
            this.mainTable.tableSearch.filters.conditions = conditions;
        },

        refreshData() {
            if (this.mainTable.tableSearch.limit) {
                this.mainTable.tableSearch.limit.conditions.pageStart = 1;
            }
            this.getYSLogs();
        },

        showDetail(row) {
            this.$refs.ysLogDetailModal.open(row);
        },

        handleRowClick(row) {
            this.showDetail(row);
        },

        copyToClipboard(text) {
            if (navigator.clipboard) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$Message.success('编号已复制到剪贴板');
                });
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                document.body.appendChild(textarea);
                textarea.select();
                try {
                    document.execCommand('copy');
                    this.$Message.success('编号已复制到剪贴板');
                } catch (err) {
                    this.$Message.error('复制失败');
                }
                document.body.removeChild(textarea);
            }
        }
    },

    mounted() {
        if (!this.mainTable.tableSearch.filters) {
            this.mainTable.tableSearch.filters = { enable: true, conditions: [] };
        }
        this.getYSLogs();
    }
};
</script>

<style lang="less" scoped>
.ys-log-management {
    background: #f5f7f9;
    min-height: calc(100vh - 100px);

    .search-wrapper {
        margin-bottom: 8px;
    }

    .stat-row {
        margin-bottom: 24px;
    }

    .stat-card {
        border: none;
        border-radius: 8px;
        transition: all 0.3s;
        
        &:hover {
            transform: translateY(-4px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .stat-content {
            display: flex;
            align-items: center;
            padding: 4px;
        }

        .stat-icon-box {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 16px;
        }

        .stat-info {
            .stat-value {
                font-size: 20px;
                font-weight: 700;
                line-height: 1.2;
                margin-bottom: 4px;
            }
            .stat-label {
                font-size: 13px;
                color: #808695;
            }
        }

        &.stat-primary {
            .stat-icon-box { background: #e8f4ff; color: #2d8cf0; }
            .stat-value { color: #2d8cf0; }
        }
        &.stat-error {
            .stat-icon-box { background: #fff1f0; color: #ed4014; }
            .stat-value { color: #ed4014; }
        }
        &.stat-warning {
            .stat-icon-box { background: #fffbe6; color: #faad14; }
            .stat-value { color: #faad14; }
        }
        &.stat-success {
            .stat-icon-box { background: #f6ffed; color: #52c41a; }
            .stat-value { color: #52c41a; }
        }
    }

    .global-tool-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        padding: 0 4px;

        .left-bar {
            display: flex;
            align-items: center;
            gap: 12px;

            .title {
                font-size: 16px;
                font-weight: 600;
                color: #17233d;
            }
            .filter-tag {
                cursor: pointer;
            }
        }
    }

    .time-column {
        .date-text { font-weight: 500; color: #17233d; }
        .time-text { font-size: 12px; color: #808695; margin-top: 2px; }
    }

    .copyable-cell {
        display: flex;
        align-items: center;
        gap: 8px;
        .code-text { font-family: 'Consolas', 'Monaco', monospace; color: #2d8cf0; }
        .copy-icon {
            color: #808695;
            cursor: pointer;
            opacity: 0;
            transition: opacity 0.2s;
        }
        &:hover .copy-icon { opacity: 1; }
    }

    .customer-info {
        .name { font-weight: 500; color: #17233d; }
        .phone {
            font-size: 12px;
            color: #808695;
            display: flex;
            align-items: center;
            gap: 4px;
            margin-top: 2px;
        }
    }

    .amount-text {
        font-family: 'Consolas', monospace;
        font-weight: 600;
        &.positive { color: #ed4014; }
    }

    .ys-info-cell {
        .order-code { font-family: monospace; color: #19be6b; font-weight: 500; }
        .order-id { font-size: 11px; color: #808695; }
    }

    .view-btn {
        color: #2d8cf0;
        &:hover { background: #f0faff; }
    }
}
</style>
